import React from 'react';
import PropTypes from 'prop-types';
import { kebabCase } from 'lodash';
import { Link } from 'react-router-dom';

import { useClassName } from 'common/hooks';
import { track } from 'utils/analytics';
import { scrollTo } from 'utils/helpers/scroll';
import { getCtaType } from 'public/components/ContentBanner/utils';
import {
  getAssetUrl,
  sanitizeCssValues,
} from 'public/helpers/contentful/utils';
import useCustomStyle from 'public/screens/CustomLandingPage/hooks';
import { toStyleObject } from 'public/screens/CustomLandingPage/utils';

const JUMP_OFFSET = -64;

function jumpToElement(url) {
  const id = String(url || '').replace('#', '');
  const element =
    document.getElementById(id) || document.getElementById(kebabCase(id));

  if (element) scrollTo(element, JUMP_OFFSET);
}

export default function FloatingWidgetCTA({ cta, phase, className, onClick }) {
  const classNames = useClassName('FloatingWidget');
  const [, style] = useCustomStyle(cta?.fields?.settings);

  if (!cta?.fields?.label) return null;

  const { label, url, icon } = cta.fields;
  const type = getCtaType(cta.fields.type);
  const iconUrl = getAssetUrl(icon);

  const attrs = {
    className: [classNames('cta'), className].filter(Boolean).join(' '),
    style: toStyleObject(sanitizeCssValues(style)),
  };

  const handleClick = (event) => {
    track('Floating widget CTA pressed', {
      label,
      url,
      type,
      phase,
      category: type,
    });

    if (type === 'jump') {
      event.preventDefault();
      jumpToElement(url);
    }

    onClick?.(event, cta);
  };

  const content = (
    <React.Fragment>
      <span className={classNames('cta-label')}>{label}</span>
      {Boolean(iconUrl) && (
        <img className={classNames('cta-icon')} src={iconUrl} alt="" />
      )}
    </React.Fragment>
  );

  if (type === 'external') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        {...attrs}>
        {content}
      </a>
    );
  }

  if (type === 'jump') {
    return (
      <a
        href={`#${String(url || '').replace('#', '')}`}
        onClick={handleClick}
        {...attrs}>
        {content}
      </a>
    );
  }

  return (
    <Link to={url || ''} onClick={handleClick} {...attrs}>
      {content}
    </Link>
  );
}

FloatingWidgetCTA.propTypes = {
  cta: PropTypes.object,
  phase: PropTypes.string,
  className: PropTypes.string,
  onClick: PropTypes.func,
};
